import { useCallback, useState } from 'react';
import { OmitStrict } from '@tanium/coreui-utils';
import { Id } from '../../types/Id';
import { ListOverflowChangeEvent } from '../../types/ListOverflowChangeEvent';
import { ListProps } from './ListProps';
import { RibbonListWrapperProps } from './RibbonListWrapper';

export type UseRibbonListWrapperProps<TId extends Id, TItem> = OmitStrict<
  RibbonListWrapperProps<TId, TItem>,
  'children' | 'isListOverflowing'
>;

interface UseRibbonListWrapperReturnType<TId extends Id, TItem> {
  ribbonListWrapperProps: OmitStrict<RibbonListWrapperProps<TId, TItem>, 'children'>;
  ribbonListProps: ListProps<TId, TItem>;
}

const DEFAULT_ITEMS_WHILE_LOADING = 3;

const loadingItemIdGetter = <TId extends Id = string>(index: number): TId =>
  `id-not-loaded-${index}` as TId;

export const useRibbonListWrapper = <TId extends Id, TItem>(
  props: UseRibbonListWrapperProps<TId, TItem>,
): UseRibbonListWrapperReturnType<TId, TItem> => {
  const {
    actions,
    actionsVerticalAlignment,
    checkboxVerticalAlignment,
    columns,
    dataGetter,
    disclosureVerticalAlignment,
    enableRowHighlight,
    isIdEmphasized,
    isItemLoaded,
    isLoading,
    isSelectionEnabled,
    itemCount,
    itemIdGetter: itemIdGetterProp,
    onSortChange,
    selectionTooltipContent,
    sort,
    totalItemCount,
    verticalAlignment,
    // selection props
    enableSelectAll,
    isSelectable,
    selection,
    selectionMode,
    onSelectionChange,
    // expansion props
    expandableContent,
    defaultExpansion,
    defaultExpansionMode,
    expansion,
    expansionMode,
    onExpansionChange,
    hideExpansionDisclosure,
    enableExpandAll,
  } = props;

  const [isListOverflowing, setIsListOverflowing] = useState(false);
  const onListOverflowChange = useCallback((event: ListOverflowChangeEvent) => {
    setIsListOverflowing(event.isListOverflowing);
  }, []);

  const loadingItemCount = itemCount === 0 ? DEFAULT_ITEMS_WHILE_LOADING : itemCount;
  const visibleItemCount = isLoading ? loadingItemCount : itemCount;

  // If `isLoading` then there will be several "dummy" loading rows. To avoid having
  // to have the consumer provide an `itemIdGetter` that provides unigue IDs for the
  // dummy rows (to avoid an error about duplicate keys), we handle providing IDs
  // for the dummy rows.
  const itemIdGetter = !isLoading ? itemIdGetterProp : loadingItemIdGetter;

  return {
    ribbonListWrapperProps: {
      actions,
      actionsVerticalAlignment,
      checkboxVerticalAlignment,
      columns,
      dataGetter,
      disclosureVerticalAlignment,
      enableRowHighlight,
      isIdEmphasized,
      isItemLoaded,
      isListOverflowing,
      isLoading,
      itemCount,
      itemIdGetter,
      onSortChange,
      selectionTooltipContent,
      sort,
      totalItemCount,
      verticalAlignment,
      // selection props
      enableSelectAll,
      isSelectable,
      isSelectionEnabled,
      selection,
      selectionMode,
      onSelectionChange,
      // expansion props
      expandableContent,
      defaultExpansion,
      defaultExpansionMode,
      expansion,
      expansionMode,
      onExpansionChange,
      hideExpansionDisclosure,
      enableExpandAll,
    },
    ribbonListProps: {
      itemCount: visibleItemCount,
      itemIdGetter,
      onListOverflowChange,
    },
  };
};
